import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  FileText,
  ClipboardList,
  Search,
  TrendingUp,
  FileCheck,
  AlertTriangle,
  Mic,
  Languages,
  Users2,
  ArrowRight,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const AILanding = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const aiCards = [
    {
      id: 'ai-insights',
      title: 'AI Insights',
      description: 'Intelligent summaries, risk scoring & anomaly detection',
      icon: <Brain size={24} />,
      path: '/ai/insights',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Smart summaries', 'Risk scoring', 'Anomaly detection'],
    },
    {
      id: 'ai-contract-drafting',
      title: 'AI Contract Drafting',
      description: 'Smart contract drafting from natural language prompts',
      icon: <FileText size={24} />,
      path: '/ai/drafting',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Smart drafting', 'Natural language', 'Template generation'],
    },
    {
      id: 'rfp-drafting',
      title: 'RFP Drafting',
      description: 'Draft, structure, and refine RFPs with AI-guided conversation',
      icon: <ClipboardList size={24} />,
      path: '/ai/rfpdrafting',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['RFP structure', 'Evaluation criteria', 'Vendor questionnaires'],
    },
    {
      id: 'ai-search-qa',
      title: 'AI Search & Q&A',
      description: 'Natural language search & intelligent queries',
      icon: <Search size={24} />,
      path: '/ai/search',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Natural language', 'Smart search', 'Q&A system'],
    },
    {
      id: 'predictive-analytics',
      title: 'Predictive Analytics',
      description: 'AI-powered renewal & dispute predictions',
      icon: <TrendingUp size={24} />,
      path: '/ai/analytics',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Renewal predictions', 'Dispute forecasting', 'Risk analysis'],
    },
    {
      id: 'document-ingestion',
      title: 'Document Ingestion',
      description: 'OCR & NLP metadata extraction from documents',
      icon: <FileCheck size={24} />,
      path: '/ai/ingestion',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['OCR processing', 'NLP extraction', 'Metadata analysis'],
    },
    {
      id: 'clause-risk-scoring',
      title: 'Clause Risk Scoring',
      description: 'AI-powered risk analysis & clause assessment',
      icon: <AlertTriangle size={24} />,
      path: '/ai/risk-scoring',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Risk analysis', 'Clause assessment', 'Compliance check'],
    },
    {
      id: 'voice-to-contract',
      title: 'Voice-to-Contract',
      description: 'Speech-to-contract generation & processing',
      icon: <Mic size={24} />,
      path: '/ai/voice-contract',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: [
        'Speech recognition',
        'Voice processing',
        'Contract generation',
      ],
    },
    {
      id: 'multilingual-translation',
      title: 'Multilingual Translation',
      description: 'Automatic contract translation & localization',
      icon: <Languages size={24} />,
      path: '/ai/translation',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Auto-translation', 'Localization', 'Multi-language'],
    },
    {
      id: 'ai-negotiation',
      title: 'AI Negotiation Support',
      description: 'Intelligent clause suggestions & negotiation strategies',
      icon: <Users2 size={24} />,
      path: '/ai/negotiation',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: [
        'Clause suggestions',
        'Negotiation strategies',
        'AI recommendations',
      ],
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  const filteredCards = aiCards.filter((card) => {
    const query = searchQuery.toLowerCase();
    return card.title.toLowerCase().includes(query);
  });

  return (
    <React.Fragment>
      <style>{`
        .ai-search-input {
          width: 300px;
        }
        @media (max-width: 768px) {
          .ai-search-input {
            width: 100%;
          }
        }
      `}</style>
      <PageBreadcrumb 
        rightContent={
          <div style={{ position: 'relative' }} className="ai-search-input">
            <Search 
              size={18} 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6B7280'
              }} 
            />
            <input
              type="text"
              placeholder="Search AI capabilities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--gold-primary)'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
        }
      />
      <PageHeader
        title="AI & Intelligence"
        description="Advanced AI-powered Contract LifeCycle Management capabilities"
      />
      <div className="page-container">
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {filteredCards.length > 0 ? (
            filteredCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: 'var(--primary-color)',
                  width: '100%',
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg || '#D6EFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px',
                    }}
                  >
                    <div
                      style={{ color: card.iconLine || '#2D7DD2' }}
                      className="asset-card-icon"
                    >
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1f2937',
                        margin: '0',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight size={20} className="asset-card-arrow" />
                </div>

                {/* Description */}
                <p
                  style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    margin: '0 0 12px 0',
                    lineHeight: '1.4',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            ))
          ) : (
            <div style={{ 
              gridColumn: '1 / -1', 
              padding: '60px 20px', 
              textAlign: 'center',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px dashed #D1D5DB'
            }}>
              <Search size={32} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#374151', margin: '0 0 8px 0' }}>
                No results found
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                We couldn't find any AI capabilities matching "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default AILanding;
